// Bitcoin Quiz tab — pub-quiz style monthly contest per city.
//
// UX is built around one rule: every state has exactly one primary
// next action. The QuizCard CTA below is the only quiz-action button
// in the whole tab; the user never has to expand panels and hunt for
// "Přihlásit". States it covers:
//
//   • no chat profile           → "Nastav přezdívku" (→ Profile tab)
//   • has profile, no team here → "Vytvořit / připojit tým a přihlásit"
//   • has 1 team in this city   → "+ Přihlásit '<team>'" (one click, no modal)
//   • has >1 team in this city  → "Přihlásit tým" (modal picker)
//   • already signed up         → "✓ Přihlášen jako <team>" + Odhlásit
//   • started/finished          → status tag, no action
//
// SignupModal unifies the three paths (pick / create / join) so the
// user resolves any team-state in one place.

import { use, useCallback, useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { evolu } from "../evolu";
import { cityName } from "../data/cities";
import { useUserPrefs } from "../hooks/usePrefs";
import { useChatProfile } from "../hooks/useChatProfile";
import {
  fetchQuizzes, fetchMyQuizTeams, fetchQuizLeaderboard, fetchQuizDetail,
  fetchAllQuizTeams, createQuizTeam, joinQuizTeam, leaveQuizTeam,
  signupQuizTeam, unsignupQuizTeam, updateQuizSignup,
  fetchQuizTeamProfile, fetchQuizSponsors, fetchQuizSeasons,
  type Quiz, type QuizTeam, type QuizTeamWithMembers, type QuizDetail,
  type QuizLeaderboard, type QuizTeamWithScore, type QuizDivision,
  type QuizTeamProfile, type QuizSponsor, type QuizResultBlock,
} from "../api";
import { CITIES } from "../data/cities";
import { formatDateTime } from "../lib/fmt";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Avatar } from "../components/Avatar";
import { IconTrophy, IconUsers, IconCheck, IconExternal } from "../components/Icons";

const MONTHS_FULL_CZ = [
  "Leden", "Únor", "Březen", "Duben", "Květen", "Červen",
  "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec",
] as const;

function periodLabel(period: string): string {
  const m = period.match(/^(\d{4})-(\d{2})$/);
  if (!m) return period;
  const idx = Math.max(0, Math.min(11, Number(m[2]) - 1));
  return `${MONTHS_FULL_CZ[idx]} ${m[1]}`;
}

function pluralMembers(n: number): string {
  return `${n} ${n === 1 ? "člen" : n < 5 ? "členové" : "členů"}`;
}

function divisionLabel(d: QuizDivision): string {
  return d === "pokrocili" ? "Pokročilí" : "Nováčci";
}

// Czech labels for the team-badge kinds defined in routes/quiz.js. The
// keys must stay in sync with BADGE_KINDS on the backend.
const BADGE_LABEL: Record<string, { label: string; emoji: string; hint: string }> = {
  first_quiz:   { emoji: "🌱", label: "První kvíz",        hint: "Tým se zúčastnil prvního kvízu." },
  "5_quizzes":  { emoji: "🎯", label: "5 kvízů",           hint: "Tým má za sebou 5 vyhodnocených kvízů." },
  "10_quizzes": { emoji: "🔥", label: "10 kvízů",          hint: "Tým má za sebou 10 vyhodnocených kvízů." },
  top_3_quiz:   { emoji: "🥉", label: "TOP 3",             hint: "Tým skončil v top 3 ve své divizi." },
  quiz_winner:  { emoji: "🏆", label: "Vítěz kvízu",       hint: "Tým vyhrál svou divizi v kvízu." },
};

export const QuizTab: FC = () => {
  const owner = use(evolu.appOwner);
  const ownerId = owner.id as string;
  const { prefs } = useUserPrefs();
  const { profile } = useChatProfile();
  const hasProfile = profile !== null;

  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [myTeams, setMyTeams] = useState<QuizTeamWithMembers[]>([]);
  const [leaderboard, setLeaderboard] = useState<QuizLeaderboard | null>(null);
  const [sponsors, setSponsors] = useState<QuizSponsor[]>([]);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [viewingTeam, setViewingTeam] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string>(prefs.cities[0] ?? "");
  // null = all-time. Defaults to the current season below, because that's
  // what the sponsors' prizes are actually awarded for.
  const [season, setSeason] = useState<number | null>(new Date().getFullYear());

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      fetchQuizzes(),
      fetchMyQuizTeams(ownerId),
      // Combined leaderboard returns one row per (team, division) — we
      // partition client-side into tabs so users see Nováčci × Pokročilí
      // side by side without two roundtrips.
      fetchQuizLeaderboard({ year: season }),
      fetchQuizSponsors(),
      fetchQuizSeasons(),
    ]).then(([qs, ts, lb, sp, yrs]) => {
      if (!mounted) return;
      setQuizzes(qs);
      setMyTeams(ts);
      setLeaderboard(lb);
      setSponsors(sp);
      setSeasons(yrs);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [ownerId, reloadKey, season]);

  // If the current calendar year has no quizzes yet, fall back to the
  // newest season that does — an empty default table looks broken.
  useEffect(() => {
    if (seasons.length === 0 || season === null) return;
    if (!seasons.includes(season)) setSeason(seasons[0] ?? null);
  }, [seasons, season]);

  const upcoming = useMemo(() => {
    const now = Date.now();
    const f = cityFilter
      ? quizzes.filter((q) => q.city === cityFilter)
      : quizzes;
    return f.filter((q) => Date.parse(q.endsAt) >= now);
  }, [quizzes, cityFilter]);

  const past = useMemo(() => {
    const now = Date.now();
    return quizzes
      .filter((q) => Date.parse(q.endsAt) < now)
      .sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt));
  }, [quizzes]);

  // Hash deep-link to Profile tab — App.tsx hashchange handler picks it up.
  const goToProfile = () => {
    window.location.hash = "#profile";
  };

  return (
    <div className="quiz-tab">
      <div className="quiz-header">
        <h1 className="quiz-title">
          <IconTrophy style={{ width: 22, height: 22, marginRight: 8, verticalAlign: -3 }} />
          Bitcoin kvíz
        </h1>
        <p className="small muted" style={{ margin: "0 0 0.8rem" }}>
          Hospodský kvíz na bitcoinová témata. Každý měsíc jeden kvíz na město. Sestav si tým a přihlas se.
        </p>
      </div>

      {!hasProfile && (
        <div className="quiz-banner">
          <div className="quiz-banner-text">
            <strong>Potřebuješ přezdívku.</strong>
            <span className="small">Pro vytvoření týmu nebo přihlášení do kvízu si nejdřív zvol jméno.</span>
          </div>
          <button className="btn btn-primary btn-sm" onClick={goToProfile}>
            Nastavit přezdívku →
          </button>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <>
          <section className="quiz-section">
            <div className="flex-row space-between" style={{ alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
              <h2 className="quiz-section-title" style={{ margin: 0 }}>Kalendář kvízů</h2>
              <select
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="small"
                aria-label="Filtr města"
              >
                <option value="">Všechna města</option>
                {CITIES.filter((c) => !c.nationwide).map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>
            {upcoming.length === 0 ? (
              <div className="empty-state">
                <p>Zatím žádné naplánované kvízy{cityFilter ? ` v ${cityName(cityFilter)}` : ""}.</p>
                <p className="small muted">
                  Až místní admin nějaký vytvoří, objeví se tady.
                </p>
              </div>
            ) : (
              <div className="quiz-list">
                {upcoming.map((q) => (
                  <QuizCard
                    key={q.id}
                    quiz={q}
                    myTeams={myTeams}
                    hasProfile={hasProfile}
                    ownerId={ownerId}
                    onChange={refresh}
                    onNeedProfile={goToProfile}
                    onOpenTeam={(teamId) => setViewingTeam(teamId)}
                  />
                ))}
              </div>
            )}
          </section>

          {sponsors.length > 0 && (
            <section className="quiz-section">
              <h2 className="quiz-section-title">Ceny v sezóně</h2>
              <SponsorsSection sponsors={sponsors} />
            </section>
          )}

          <section className="quiz-section">
            <div className="flex-row space-between" style={{ alignItems: "center", flexWrap: "wrap", gap: "0.4rem" }}>
              <h2 className="quiz-section-title" style={{ margin: 0 }}>Moje týmy</h2>
              {hasProfile && (
                <button className="btn btn-sm btn-ghost" onClick={() => setCreateTeamOpen(true)}>
                  + Nový tým
                </button>
              )}
            </div>
            {!hasProfile ? (
              <div className="empty-state">
                <p className="small muted">
                  Po nastavení přezdívky můžeš vytvořit tým, nebo se připojit k existujícímu při přihlašování na kvíz.
                </p>
              </div>
            ) : myTeams.length === 0 ? (
              <div className="empty-state">
                <p>Zatím nejsi v žádném týmu.</p>
                <p className="small muted">
                  Můžeš si vytvořit nový teď, nebo až přímo na nadcházejícím kvízu — přihlašovací dialog ti to nabídne.
                </p>
              </div>
            ) : (
              <div className="quiz-team-list">
                {myTeams.map((t) => (
                  <MyTeamCard key={t.id} team={t} ownerId={ownerId} onChange={refresh} />
                ))}
              </div>
            )}
          </section>

          <section className="quiz-section">
            <div className="flex-row space-between" style={{ alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
              <h2 className="quiz-section-title" style={{ margin: 0 }}>Žebříček (celá ČR)</h2>
              <select
                value={season === null ? "" : String(season)}
                onChange={(e) => setSeason(e.target.value === "" ? null : Number(e.target.value))}
                className="small"
                aria-label="Sezóna"
              >
                {seasons.map((y) => (
                  <option key={y} value={String(y)}>Sezóna {y}</option>
                ))}
                <option value="">Celkově</option>
              </select>
            </div>
            {!leaderboard || leaderboard.teams.length === 0 ? (
              <div className="empty-state">
                <p className="small muted">
                  {season === null
                    ? "Žebříček se vyplní po prvním vyhodnoceném kvízu."
                    : `V sezóně ${season} zatím není vyhodnocený žádný kvíz.`}
                </p>
              </div>
            ) : (
              <LeaderboardTabs board={leaderboard} onOpenTeam={(id) => setViewingTeam(id)} />
            )}
          </section>

          {past.length > 0 && (
            <section className="quiz-section">
              <h2 className="quiz-section-title">Proběhlé kvízy</h2>
              <div className="quiz-list">
                {past.map((q) => (
                  <QuizCard
                    key={q.id}
                    quiz={q}
                    myTeams={myTeams}
                    hasProfile={hasProfile}
                    ownerId={ownerId}
                    onChange={refresh}
                    onNeedProfile={goToProfile}
                    onOpenTeam={(teamId) => setViewingTeam(teamId)}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {createTeamOpen && (
        <CreateTeamModal
          ownerId={ownerId}
          onClose={() => setCreateTeamOpen(false)}
          onCreated={() => { setCreateTeamOpen(false); refresh(); }}
        />
      )}
      {viewingTeam && (
        <TeamProfileModal
          teamId={viewingTeam}
          onClose={() => setViewingTeam(null)}
        />
      )}
    </div>
  );
};

// ── Quiz card with state-aware single CTA ─────────────────────
const QuizCard: FC<{
  quiz: Quiz;
  /** All teams the user is in. Teams are nationwide — server enforces
   *  "one quiz per period per team" so a sign-up may still 409 if the
   *  team already plays this month elsewhere. */
  myTeams: QuizTeamWithMembers[];
  hasProfile: boolean;
  ownerId: string;
  onChange: () => void;
  onNeedProfile: () => void;
  onOpenTeam: (teamId: string) => void;
}> = ({ quiz, myTeams, hasProfile, ownerId, onChange, onNeedProfile, onOpenTeam }) => {
  const [detail, setDetail] = useState<QuizDetail | null>(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [editSignup, setEditSignup] = useState<QuizTeamWithScore | null>(null);
  const [showTeams, setShowTeams] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [busy, setBusy] = useState(false);

  const started = Date.parse(quiz.startsAt) <= Date.now();
  const finished = Date.parse(quiz.endsAt) <= Date.now();
  // A published board on a running quiz is a live scoreboard — the room
  // is looking at it between rounds, so poll while it matters and stop
  // the moment the night is over.
  const liveBoard = started && !finished && quiz.resultsPublic;

  useEffect(() => {
    let mounted = true;
    const load = () => { void fetchQuizDetail(quiz.id).then((d) => { if (mounted) setDetail(d); }); };
    load();
    if (!liveBoard) return () => { mounted = false; };
    const id = setInterval(load, 30_000);
    return () => { mounted = false; clearInterval(id); };
  }, [quiz.id, quiz.teamCount, quiz.scored, liveBoard]);

  const myTeamSignedUp: QuizTeamWithScore | null = useMemo(() => {
    if (!detail) return null;
    return detail.teams.find((t) => myTeams.some((m) => m.id === t.id)) ?? null;
  }, [detail, myTeams]);

  const unsign = async () => {
    if (!myTeamSignedUp) return;
    setBusy(true);
    const r = await unsignupQuizTeam(quiz.id, ownerId, myTeamSignedUp.id);
    setBusy(false);
    if (!r.ok) { alert(r.error ?? "Selhalo odhlášení."); return; }
    onChange();
  };

  let cta: ReactNode;
  if (finished) {
    cta = quiz.scored ? (
      <span className="quiz-cta-tag scored"><IconCheck style={{ width: 14, height: 14 }} /> Vyhodnoceno</span>
    ) : (
      <span className="quiz-cta-tag muted-tag">Proběhlo, čeká na skóre</span>
    );
  } else if (started) {
    cta = quiz.resultsPublic
      ? <span className="quiz-cta-tag scored">Právě probíhá · průběžné výsledky</span>
      : <span className="quiz-cta-tag">Právě probíhá</span>;
  } else if (!hasProfile) {
    cta = (
      <button className="btn btn-primary btn-sm" onClick={onNeedProfile}>
        Nastav přezdívku
      </button>
    );
  } else if (myTeamSignedUp) {
    cta = (
      <div className="quiz-cta-double">
        <span className="quiz-cta-tag scored">
          <IconCheck style={{ width: 14, height: 14 }} /> Přihlášen jako <strong>{myTeamSignedUp.name}</strong>
          <span className="pill" style={{ marginLeft: 6 }}>
            {divisionLabel(myTeamSignedUp.division)}
          </span>
          {myTeamSignedUp.headcount !== null && (
            <span className="pill" style={{ marginLeft: 4 }}>
              přijde {myTeamSignedUp.headcount}
            </span>
          )}
        </span>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setEditSignup(myTeamSignedUp)}
          disabled={busy}
        >
          Upravit
        </button>
        <button className="btn btn-ghost btn-sm" onClick={unsign} disabled={busy}>
          Odhlásit
        </button>
      </div>
    );
  } else if (myTeams.length >= 1) {
    cta = (
      <button className="btn btn-primary btn-sm" onClick={() => setSignupOpen(true)}>
        Přihlásit tým
      </button>
    );
  } else {
    cta = (
      <button className="btn btn-primary btn-sm" onClick={() => setSignupOpen(true)}>
        Vytvořit / připojit tým a přihlásit
      </button>
    );
  }

  const teamCount = detail?.teams.length ?? quiz.teamCount;

  return (
    <div className="card quiz-card">
      <div className="quiz-card-head-static">
        <div className="quiz-card-period small muted">
          {periodLabel(quiz.period)} · {cityName(quiz.city)}
        </div>
        <div className="quiz-card-title">{quiz.title}</div>
        <div className="small muted" style={{ marginTop: 4 }}>
          {formatDateTime(quiz.startsAt)} · {quiz.location}
        </div>
        {quiz.url && (
          <div className="small" style={{ marginTop: 4 }}>
            <a href={quiz.url} target="_blank" rel="noreferrer">
              <IconExternal style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4 }} />
              Více info
            </a>
          </div>
        )}
        {quiz.description && (
          <p className="small quiz-card-desc" style={{ whiteSpace: "pre-wrap" }}>{quiz.description}</p>
        )}
      </div>

      <div className="quiz-card-cta-row">
        {cta}
        <button
          className="quiz-card-teams-toggle"
          onClick={() => setShowTeams((v) => !v)}
          disabled={teamCount === 0}
          title={teamCount === 0 ? "Zatím žádný přihlášený tým" : "Zobrazit přihlášené týmy"}
        >
          <IconUsers style={{ width: 12, height: 12, marginRight: 4 }} />
          {teamCount} {teamCount === 0 ? "týmů" : showTeams ? "▴" : "▾"}
        </button>
      </div>

      {/* Scoreboard. Present as soon as the backend says results are
          visible — after the quiz ends, or the moment the moderator
          publishes a live board mid-quiz. */}
      {detail && detail.results.length > 0 && (
        <ResultsBoard
          blocks={detail.results}
          live={!finished}
          rounds={quiz.rounds}
          myTeamIds={myTeams.map((m) => m.id)}
          onOpenTeam={onOpenTeam}
        />
      )}

      {showTeams && detail && detail.teams.length > 0 && (
        <ul className="quiz-team-signup-list">
          {detail.teams.map((t) => {
            const mine = myTeams.some((m) => m.id === t.id);
            return (
              <li key={t.id}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => onOpenTeam(t.id)}
                      style={{ background: "none", border: 0, padding: 0, color: "inherit", cursor: "pointer", textAlign: "left", font: "inherit" }}
                      title="Zobrazit profil týmu"
                    >
                      {t.name}
                    </button>
                    {" "}{mine && <span className="quiz-team-mine-tag">tvůj tým</span>}
                  </div>
                  <div className="small muted">
                    {t.createdByOwnerId === null
                      ? <span className="pill">zapsán organizátorem</span>
                      : pluralMembers(t.memberCount)}
                    {" · "}
                    <span className="pill">{divisionLabel(t.division)}</span>
                    {t.headcount !== null && (
                      <> · <span className="pill">přijde {t.headcount}</span></>
                    )}
                    {t.score !== null && <> · skóre <strong>{t.score}</strong></>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Question archive on past quizzes — only shows up after endsAt
          (backend gates the public reveal). */}
      {finished && detail && detail.questions.length > 0 && (
        <div style={{ marginTop: "0.6rem" }}>
          <button
            type="button"
            className="quiz-card-teams-toggle"
            onClick={() => setShowQuestions((v) => !v)}
          >
            📚 Otázky &amp; odpovědi ({detail.questions.length}) {showQuestions ? "▴" : "▾"}
          </button>
          {showQuestions && (
            <ol className="quiz-questions-list" style={{ marginTop: "0.5rem", paddingLeft: "1.25rem" }}>
              {detail.questions.map((q) => (
                <li key={q.id} style={{ marginBottom: "0.6rem" }}>
                  <div style={{ fontWeight: 600 }}>{q.question}</div>
                  <div className="small">
                    <strong>Odpověď:</strong> {q.answer}
                  </div>
                  {q.sourceUrl && (
                    <div className="small muted">
                      Zdroj: <a href={q.sourceUrl} target="_blank" rel="noreferrer">{q.sourceUrl}</a>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {signupOpen && (
        <SignupModal
          quiz={quiz}
          myTeams={myTeams}
          ownerId={ownerId}
          onClose={() => setSignupOpen(false)}
          onDone={() => { setSignupOpen(false); onChange(); }}
        />
      )}
      {editSignup && (
        <EditSignupModal
          quizId={quiz.id}
          ownerId={ownerId}
          team={editSignup}
          onClose={() => setEditSignup(null)}
          onDone={() => { setEditSignup(null); onChange(); }}
        />
      )}
    </div>
  );
};

// ── Scoreboard for one quiz night ────────────────────────────
// One table per division, because they're two separate competitions.
// Teams tied on both score and tiebreak share a place and render as
// "2.–3." rather than being silently ordered by row id.
const MEDALS = ["🥇", "🥈", "🥉"] as const;

const ResultsBoard: FC<{
  blocks: QuizResultBlock[];
  /** Quiz still running — the board is a live snapshot, not the result. */
  live: boolean;
  rounds: number;
  myTeamIds: string[];
  onOpenTeam: (teamId: string) => void;
}> = ({ blocks, live, rounds, myTeamIds, onOpenTeam }) => (
  <div className="quiz-results" style={{ marginTop: "0.7rem" }}>
    <div className="small muted" style={{ marginBottom: "0.35rem" }}>
      {live ? "Průběžné pořadí — kvíz ještě běží" : "Výsledky"}
    </div>
    {blocks.map((block) => (
      <div key={block.division} style={{ marginBottom: "0.7rem" }}>
        <div className="small" style={{ fontWeight: 600, marginBottom: 2 }}>
          {divisionLabel(block.division)}
        </div>
        <div className="quiz-leaderboard">
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left", width: "3.2em" }}>#</th>
                <th style={{ textAlign: "left" }}>Tým</th>
                {rounds > 0 && (
                  <th style={{ textAlign: "right" }} title="Body po kolech">Kola</th>
                )}
                <th style={{ textAlign: "right" }}>Body</th>
              </tr>
            </thead>
            <tbody>
              {block.teams.map((t) => {
                const mine = myTeamIds.includes(t.teamId);
                const medal = t.rank <= 3 ? MEDALS[t.rank - 1] : null;
                return (
                  <tr key={t.teamId}>
                    <td style={{ fontFamily: "var(--mono)" }}>
                      {medal ?? `${t.rank}.`}
                      {t.sharedRank && <span className="small muted" title="Dělené místo">=</span>}
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => onOpenTeam(t.teamId)}
                        style={{ background: "none", border: 0, padding: 0, color: "inherit", cursor: "pointer", textAlign: "left", font: "inherit", fontWeight: mine ? 700 : 400 }}
                        title="Zobrazit profil týmu"
                      >
                        {t.name}
                      </button>
                      {mine && <span className="quiz-team-mine-tag" style={{ marginLeft: 6 }}>tvůj tým</span>}
                    </td>
                    {rounds > 0 && (
                      <td className="small muted" style={{ textAlign: "right", fontFamily: "var(--mono)" }}>
                        {t.rounds.length > 0 ? t.rounds.join("+") : "—"}
                      </td>
                    )}
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{t.score}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    ))}
  </div>
);

// ── Unified signup modal: pick / create / join, all in one ───
const SignupModal: FC<{
  quiz: Quiz;
  myTeams: QuizTeamWithMembers[];
  ownerId: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ quiz, myTeams, ownerId, onClose, onDone }) => {
  type Mode = "pick" | "create" | "join";
  const initialMode: Mode = myTeams.length > 0 ? "pick" : "create";
  const [mode, setMode] = useState<Mode>(initialMode);
  const [name, setName] = useState("");
  const [division, setDivision] = useState<QuizDivision>("novacci");
  const [headcountStr, setHeadcountStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [allTeams, setAllTeams] = useState<QuizTeam[]>([]);
  const [allTeamsLoading, setAllTeamsLoading] = useState(false);

  // Lazy-load the nationwide team list when user opens the "join" tab.
  useEffect(() => {
    if (mode !== "join") return;
    setAllTeamsLoading(true);
    fetchAllQuizTeams().then((ts) => {
      setAllTeams(ts);
      setAllTeamsLoading(false);
    });
  }, [mode]);

  const headcountOpt = useMemo(() => {
    if (headcountStr.trim() === "") return undefined;
    const n = Number(headcountStr);
    return Number.isFinite(n) && Number.isInteger(n) && n >= 1 && n <= 50 ? n : null;
  }, [headcountStr]);

  const pick = async (teamId: string) => {
    if (headcountOpt === null) { setErr("Účast 1–50."); return; }
    setBusy(true); setErr("");
    const r = await signupQuizTeam(quiz.id, ownerId, teamId, {
      division,
      ...(headcountOpt !== undefined ? { headcount: headcountOpt } : {}),
    });
    setBusy(false);
    if (!r.ok) { setErr(r.error ?? "Selhalo přihlášení."); return; }
    onDone();
  };

  // Create-and-signup is two API calls but one user action: if step 2 fails
  // we keep the team (it's a useful side effect) and surface the error so
  // the user can retry with the now-existing team via the "Můj tým" tab.
  const create = async () => {
    if (name.trim().length < 2) { setErr("Zadej alespoň 2 znaky."); return; }
    if (headcountOpt === null) { setErr("Účast 1–50."); return; }
    setBusy(true); setErr("");
    const made = await createQuizTeam(ownerId, name);
    if (!made.ok) { setBusy(false); setErr(made.error); return; }
    const sign = await signupQuizTeam(quiz.id, ownerId, made.id, {
      division,
      ...(headcountOpt !== undefined ? { headcount: headcountOpt } : {}),
    });
    setBusy(false);
    if (!sign.ok) { setErr(sign.error ?? "Tým vytvořen, ale přihlášení selhalo."); return; }
    onDone();
  };

  const joinAndSignup = async (teamId: string) => {
    if (headcountOpt === null) { setErr("Účast 1–50."); return; }
    setBusy(true); setErr("");
    const join = await joinQuizTeam(teamId, ownerId);
    if (!join.ok) { setBusy(false); setErr(join.error ?? "Selhalo připojení."); return; }
    const sign = await signupQuizTeam(quiz.id, ownerId, teamId, {
      division,
      ...(headcountOpt !== undefined ? { headcount: headcountOpt } : {}),
    });
    setBusy(false);
    if (!sign.ok) { setErr(sign.error ?? "Připojeno, ale přihlášení selhalo."); return; }
    onDone();
  };

  const joinable = allTeams.filter((t) => !myTeams.some((m) => m.id === t.id));

  return (
    <div className="pow-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pow-container quiz-signup-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pow-close" onClick={onClose} aria-label="Zavřít">×</button>
        <h3 style={{ margin: "0 0 0.2rem" }}>Přihlásit do kvízu</h3>
        <p className="small muted" style={{ margin: "0 0 1rem" }}>
          {quiz.title} · {periodLabel(quiz.period)} · {cityName(quiz.city)}
        </p>

        <div className="quiz-signup-shared" style={{ marginBottom: "0.8rem" }}>
          <div className="field">
            <label className="small">Divize</label>
            <div className="row-actions" style={{ gap: "0.4rem" }}>
              <button
                type="button"
                className={`btn btn-sm ${division === "novacci" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setDivision("novacci")}
              >Nováčci</button>
              <button
                type="button"
                className={`btn btn-sm ${division === "pokrocili" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setDivision("pokrocili")}
              >Pokročilí</button>
            </div>
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              Žebříček se počítá zvlášť pro každou divizi. Změnit jde do startu kvízu.
            </p>
          </div>
          <div className="field">
            <label className="small">Přijde nás (potvrzení účasti)</label>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={50}
              value={headcountStr}
              onChange={(e) => { setHeadcountStr(e.target.value); setErr(""); }}
              placeholder="např. 4"
              style={{ maxWidth: 120 }}
            />
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              Volitelné — organizátor uvidí součet a může podle toho naplánovat kapacitu.
            </p>
          </div>
        </div>

        <div className="quiz-signup-tabs">
          {myTeams.length > 0 && (
            <button
              className={mode === "pick" ? "active" : ""}
              onClick={() => { setMode("pick"); setErr(""); }}
            >
              Můj tým
            </button>
          )}
          <button
            className={mode === "create" ? "active" : ""}
            onClick={() => { setMode("create"); setErr(""); }}
          >
            + Nový tým
          </button>
          <button
            className={mode === "join" ? "active" : ""}
            onClick={() => { setMode("join"); setErr(""); }}
          >
            Připojit se k týmu
          </button>
        </div>

        {mode === "pick" && (
          <ul className="quiz-signup-options">
            {myTeams.map((t) => (
              <li key={t.id}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <div className="small muted">{pluralMembers(t.memberCount)}</div>
                </div>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => pick(t.id)}
                  disabled={busy}
                >
                  Přihlásit
                </button>
              </li>
            ))}
          </ul>
        )}

        {mode === "create" && (
          <div className="form">
            <div className="field">
              <label>Název týmu</label>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()}
                placeholder="např. Satoshi's Disciples"
                maxLength={60}
              />
              <p className="small muted" style={{ margin: "4px 0 0" }}>
                Tým založíš a hned přihlásíš jedním kliknutím.
              </p>
            </div>
            <div className="row-actions">
              <button className="btn btn-primary" onClick={create} disabled={busy}>
                {busy ? "Pracuju…" : "Vytvořit a přihlásit"}
              </button>
            </div>
          </div>
        )}

        {mode === "join" && (
          allTeamsLoading ? <LoadingSpinner /> : joinable.length === 0 ? (
            <p className="small muted">Zatím nejsou žádné jiné týmy. Zkus záložku „Nový tým".</p>
          ) : (
            <ul className="quiz-signup-options">
              {joinable.map((t) => (
                <li key={t.id}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{t.name}</div>
                    <div className="small muted">{pluralMembers(t.memberCount)}</div>
                  </div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => joinAndSignup(t.id)}
                    disabled={busy}
                  >
                    Připojit a přihlásit
                  </button>
                </li>
              ))}
            </ul>
          )
        )}

        {err && <p className="error small" style={{ marginTop: 8 }}>{err}</p>}
      </div>
    </div>
  );
};

// ── My team card (manage members + leave) ─────────────────────
const MyTeamCard: FC<{
  team: QuizTeamWithMembers;
  ownerId: string;
  onChange: () => void;
}> = ({ team, ownerId, onChange }) => {
  const [busy, setBusy] = useState(false);

  const leave = async () => {
    if (!window.confirm(`Opustit tým „${team.name}"?`)) return;
    setBusy(true);
    const r = await leaveQuizTeam(team.id, ownerId);
    setBusy(false);
    if (!r.ok) { alert(r.error ?? "Selhalo opuštění."); return; }
    onChange();
  };

  return (
    <div className="card quiz-team-card">
      <div className="flex-row space-between" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="quiz-team-name">{team.name}</div>
          <div className="small muted">{pluralMembers(team.memberCount)}</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={leave} disabled={busy}>
          Opustit
        </button>
      </div>
      <ul className="quiz-team-members">
        {team.members.map((m) => (
          <li key={m.ownerId}>
            <Avatar src={m.avatar} name={m.displayName} className="chat-avatar" />
            <span>{m.displayName}</span>
            {m.ownerId === team.createdByOwnerId && <span className="pill">kapitán</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── Standalone create-team modal — used from "Moje týmy" section.
// Teams are nationwide so this modal is name-only; the signup-modal
// path is also name-only.
const CreateTeamModal: FC<{
  ownerId: string;
  onClose: () => void;
  onCreated: () => void;
}> = ({ ownerId, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (name.trim().length < 2) { setErr("Zadej alespoň 2 znaky."); return; }
    setBusy(true); setErr("");
    const r = await createQuizTeam(ownerId, name);
    setBusy(false);
    if (!r.ok) { setErr(r.error); return; }
    onCreated();
  };

  return (
    <div className="pow-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pow-container quiz-create-team-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pow-close" onClick={onClose} aria-label="Zavřít">×</button>
        <h3 style={{ margin: "0 0 0.6rem" }}>Nový kvíz tým</h3>
        <p className="small muted" style={{ margin: "0 0 0.9rem" }}>
          Tým je celorepublikový — může se přihlásit na kvíz v jakémkoli městě.
        </p>
        <div className="form">
          <div className="field">
            <label>Název týmu</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="např. Satoshi's Disciples"
              maxLength={60}
            />
          </div>
          {err && <p className="error small">{err}</p>}
          <div className="row-actions">
            <button className="btn btn-primary" onClick={submit} disabled={busy}>
              {busy ? "Vytvářím…" : "Vytvořit tým"}
            </button>
            <button className="btn btn-ghost" onClick={onClose}>Zrušit</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Leaderboard with division tabs — server returns one row per
// (team, division), so we partition client-side. Both tabs are visible
// even if one is empty, so the user always understands the split exists.
const LeaderboardTabs: FC<{
  board: QuizLeaderboard;
  onOpenTeam: (teamId: string) => void;
}> = ({ board, onOpenTeam }) => {
  const [tab, setTab] = useState<QuizDivision>("novacci");
  const novacci = useMemo(() => board.teams.filter((t) => t.division === "novacci"), [board]);
  const pokrocili = useMemo(() => board.teams.filter((t) => t.division === "pokrocili"), [board]);
  const active = tab === "novacci" ? novacci : pokrocili;
  return (
    <div>
      <div className="quiz-signup-tabs" style={{ marginBottom: "0.6rem" }}>
        <button className={tab === "novacci" ? "active" : ""} onClick={() => setTab("novacci")}>
          Nováčci ({novacci.length})
        </button>
        <button className={tab === "pokrocili" ? "active" : ""} onClick={() => setTab("pokrocili")}>
          Pokročilí ({pokrocili.length})
        </button>
      </div>
      {active.length === 0 ? (
        <p className="small muted">V této divizi zatím žádný vyhodnocený výsledek.</p>
      ) : (
        <div className="quiz-leaderboard">
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>#</th>
                <th style={{ textAlign: "left" }}>Tým</th>
                <th style={{ textAlign: "right" }} title="Počet vyhodnocených kvízů">Kvízů</th>
                <th style={{ textAlign: "right" }}>Σ skóre</th>
              </tr>
            </thead>
            <tbody>
              {active.map((t) => (
                <tr key={`${t.teamId}-${t.division}`}>
                  {/* Rank comes from the server so ties share a place the
                      same way they do on the night's scoreboard — a row
                      index would quietly invent an order. */}
                  <td style={{ fontFamily: "var(--mono)" }}>
                    {t.rank <= 3 ? MEDALS[t.rank - 1] : `${t.rank}.`}
                    {t.sharedRank && <span className="small muted" title="Dělené místo">=</span>}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => onOpenTeam(t.teamId)}
                      style={{ background: "none", border: 0, padding: 0, color: "inherit", cursor: "pointer", textAlign: "left", font: "inherit" }}
                      title="Zobrazit profil týmu"
                    >
                      {t.teamName}
                    </button>
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "var(--mono)" }}>{t.quizCount}</td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>{t.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Season sponsors / prizes section.
const SponsorsSection: FC<{ sponsors: QuizSponsor[] }> = ({ sponsors }) => (
  <div className="quiz-sponsors">
    {sponsors.map((s) => {
      const inner = (
        <>
          {s.logoUrl && (
            <img src={s.logoUrl} alt={s.name} className="quiz-sponsor-logo" loading="lazy" />
          )}
          <div className="quiz-sponsor-text">
            <strong>{s.name}</strong>
            {s.prizeLabel && <div className="small">{s.prizeLabel}</div>}
          </div>
        </>
      );
      return s.sponsorUrl ? (
        <a
          key={s.id}
          href={s.sponsorUrl}
          target="_blank"
          rel="noreferrer"
          className="card quiz-sponsor-card"
        >{inner}</a>
      ) : (
        <div key={s.id} className="card quiz-sponsor-card">{inner}</div>
      );
    })}
  </div>
);

// ── Edit-signup modal: captain changes division / headcount before
// the quiz starts (backend rejects updates after starts_at).
const EditSignupModal: FC<{
  quizId: string;
  ownerId: string;
  team: QuizTeamWithScore;
  onClose: () => void;
  onDone: () => void;
}> = ({ quizId, ownerId, team, onClose, onDone }) => {
  const [division, setDivision] = useState<QuizDivision>(team.division);
  const [headcountStr, setHeadcountStr] = useState(team.headcount?.toString() ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    const patch: { division?: QuizDivision; headcount?: number | null } = {};
    if (division !== team.division) patch.division = division;
    if (headcountStr.trim() === "") patch.headcount = null;
    else {
      const n = Number(headcountStr);
      if (!Number.isInteger(n) || n < 1 || n > 50) {
        setBusy(false);
        setErr("Účast 1–50.");
        return;
      }
      patch.headcount = n;
    }
    if (Object.keys(patch).length === 0) { setBusy(false); onClose(); return; }
    const r = await updateQuizSignup(quizId, ownerId, team.id, patch);
    setBusy(false);
    if (!r.ok) { setErr(r.error ?? "Selhalo uložení."); return; }
    onDone();
  };

  return (
    <div className="pow-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pow-container quiz-signup-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pow-close" onClick={onClose} aria-label="Zavřít">×</button>
        <h3 style={{ margin: "0 0 0.6rem" }}>Upravit přihlášení: {team.name}</h3>
        <div className="form">
          <div className="field">
            <label className="small">Divize</label>
            <div className="row-actions" style={{ gap: "0.4rem" }}>
              <button
                type="button"
                className={`btn btn-sm ${division === "novacci" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setDivision("novacci")}
              >Nováčci</button>
              <button
                type="button"
                className={`btn btn-sm ${division === "pokrocili" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setDivision("pokrocili")}
              >Pokročilí</button>
            </div>
          </div>
          <div className="field">
            <label className="small">Přijde nás</label>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={50}
              value={headcountStr}
              onChange={(e) => setHeadcountStr(e.target.value)}
              placeholder="(neuvedeno)"
              style={{ maxWidth: 120 }}
            />
          </div>
          {err && <p className="error small">{err}</p>}
          <div className="row-actions">
            <button className="btn btn-primary" onClick={save} disabled={busy}>
              {busy ? "Ukládám…" : "Uložit"}
            </button>
            <button className="btn btn-ghost" onClick={onClose}>Zrušit</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Team profile modal: history (per quiz: city, period, division,
// score, rank), badges, members. Public — anyone can open it from a
// team name on the leaderboard or signup list.
const TeamProfileModal: FC<{
  teamId: string;
  onClose: () => void;
}> = ({ teamId, onClose }) => {
  const [data, setData] = useState<QuizTeamProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchQuizTeamProfile(teamId).then((p) => {
      if (!mounted) return;
      setData(p);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [teamId]);

  return (
    <div className="pow-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pow-container quiz-team-profile-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pow-close" onClick={onClose} aria-label="Zavřít">×</button>
        {loading ? <LoadingSpinner /> : !data ? (
          <p className="error small">Profil týmu nelze načíst.</p>
        ) : (
          <>
            <h3 style={{ margin: "0 0 0.2rem" }}>{data.team.name}</h3>
            <div className="small muted" style={{ margin: "0 0 0.8rem" }}>
              {pluralMembers(data.team.memberCount)}
              {" · "}{data.totals.scoredQuizzes} vyhodnocených kvízů
              {" · "}Σ {data.totals.totalScore} bodů
              {data.totals.wins > 0 && <> · {data.totals.wins}× vítěz</>}
              {data.totals.podiums > 0 && <> · {data.totals.podiums}× top 3</>}
            </div>

            {data.badges.length > 0 && (
              <div className="quiz-team-badges" style={{ marginBottom: "1rem" }}>
                {data.badges.map((b, i) => {
                  const meta = BADGE_LABEL[b.kind];
                  const title = meta ? meta.hint + (b.contextQuizTitle ? ` — ${b.contextQuizTitle}` : "") : b.kind;
                  return (
                    <span key={`${b.kind}-${b.contextQuizId ?? ""}-${i}`} className="pill" title={title}>
                      {meta?.emoji ?? "🎖"} {meta?.label ?? b.kind}
                    </span>
                  );
                })}
              </div>
            )}

            <h4 style={{ margin: "0 0 0.4rem" }}>Členové</h4>
            <ul className="quiz-team-members">
              {data.members.map((m) => (
                <li key={m.ownerId}>
                  <Avatar src={m.avatar} name={m.displayName} className="chat-avatar" />
                  <span>{m.displayName}</span>
                  {m.ownerId === data.team.createdByOwnerId && <span className="pill">kapitán</span>}
                </li>
              ))}
            </ul>

            <h4 style={{ margin: "0.8rem 0 0.4rem" }}>Historie</h4>
            {data.history.length === 0 ? (
              <p className="small muted">Tým se zatím nezúčastnil žádného kvízu.</p>
            ) : (
              <ul className="quiz-team-history">
                {data.history.map((h) => (
                  <li key={h.quizId} style={{ marginBottom: "0.45rem" }}>
                    <div style={{ fontWeight: 600 }}>
                      {h.title}
                      <span className="small muted" style={{ marginLeft: 6 }}>
                        {periodLabel(h.period)} · {cityName(h.city)}
                      </span>
                    </div>
                    <div className="small">
                      <span className="pill">{divisionLabel(h.division)}</span>
                      {h.score !== null ? (
                        <>
                          {" · skóre "}<strong>{h.score}</strong>
                          {h.rank !== null && (
                            <>
                              {" · "}
                              <strong>{h.rank}.{h.sharedRank ? "–" : ""}</strong>
                              {h.sharedRank && <span title="Dělené místo"> (děleno)</span>}
                              {" z "}{h.participantCount}
                            </>
                          )}
                        </>
                      ) : (
                        <span className="muted"> · čeká na vyhodnocení</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};
